import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Info } from "lucide-react";
import { LABOR, LABOR_CATS, money, slug } from "../../lib/data";
import { Reveal, Eyebrow } from "./Reveal";

const Estimator = ({ picked, onBook, onClear }) => {
  const total = picked.reduce((s, n) => s + (LABOR.find((l) => l.name === n)?.price ?? 0), 0);
  return (
    <div data-testid="labor-estimator" className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--surface)] p-7">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[var(--vio)]/30 blur-3xl" />
      <div className="relative text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400">Labor estimate</div>
      <motion.div key={total} initial={{ y: 12, opacity: 0.4 }} animate={{ y: 0, opacity: 1 }} data-testid="labor-estimate-total" className="font-race relative mt-2 text-7xl text-neon">
        {money(total)}
      </motion.div>
      <div className="relative mt-1 text-sm text-zinc-300">{picked.length ? `${picked.length} repair${picked.length > 1 ? "s" : ""} selected` : "Tap repairs in the chart to add them"}</div>
      <ul className="relative mt-5 space-y-2 text-sm text-zinc-400">
        <li className="flex gap-2"><Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--mag)]" />Travel fee $1 per mile · labor only, parts extra</li>
        <li className="flex gap-2"><Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--mag)]" />Vehicle-specific written quote before work. Bundled repairs may share labor.</li>
      </ul>
      <div className="relative mt-6 flex gap-3">
        <button data-testid="labor-book-selected-button" disabled={!picked.length} onClick={onBook} className="btn-neon flex flex-1 items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-bold uppercase tracking-wider text-white disabled:opacity-40">
          Book these <ArrowRight className="h-4 w-4" />
        </button>
        {picked.length > 0 && (
          <button data-testid="labor-clear-button" onClick={onClear} className="rounded-full border border-white/15 px-5 text-sm font-semibold text-zinc-300 hover:text-white">Clear</button>
        )}
      </div>
    </div>
  );
};

export const LaborChart = ({ onBookRepairs }) => {
  const [cat, setCat] = useState("All");
  const [picked, setPicked] = useState([]);
  const rows = useMemo(() => LABOR.filter((l) => cat === "All" || l.cat === cat), [cat]);
  const toggle = (n) => setPicked((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));

  return (
    <section id="labor" data-testid="labor-section" className="relative border-t border-white/10 bg-[#0a0a10] py-28 lg:py-40">
      <div className="grid-lines absolute inset-0 opacity-50 [mask-image:linear-gradient(to_bottom,black,transparent)]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <Eyebrow num="02">Labor guide</Eyebrow>
              <h2 className="font-race mt-6 text-6xl leading-[0.9] text-white sm:text-7xl">
                Honest <span className="text-chrome">labor.</span><br />No <span className="text-neon">surprises.</span>
              </h2>
              <p className="mt-6 max-w-md text-base text-zinc-300 md:text-lg">$85/hr labor rate. Insured. Pick the repairs you need for a quick labor estimate.</p>
            </Reveal>
            <Reveal delay={0.1} className="mt-10">
              <Estimator picked={picked} onClear={() => setPicked([])} onBook={() => onBookRepairs(picked)} />
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="mb-6 flex flex-wrap gap-2">
            {LABOR_CATS.map((c) => (
              <button key={c} data-testid={`labor-filter-${slug(c)}`} onClick={() => setCat(c)} className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] transition-colors ${cat === c ? "border-transparent bg-white text-black" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="overflow-hidden rounded-3xl border border-white/10" data-testid="labor-table">
            <div className="flex justify-between border-b border-white/10 bg-white/[0.03] px-6 py-4 text-xs font-bold uppercase tracking-[0.25em] text-zinc-400">
              <span>Service</span><span>Labor price</span>
            </div>
            {rows.map((l, i) => {
              const on = picked.includes(l.name);
              return (
                <motion.button
                  layout
                  key={l.name}
                  data-testid={`labor-row-${slug(l.name)}`}
                  onClick={() => toggle(l.name)}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.03, 0.3) }}
                  className={`group flex w-full items-center justify-between gap-4 border-b border-white/[0.06] px-6 py-5 text-left transition-colors last:border-0 ${on ? "bg-[var(--mag)]/10" : "hover:bg-white/[0.03]"}`}
                >
                  <span className="flex items-center gap-4">
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors ${on ? "border-transparent bg-neon" : "border-white/25 group-hover:border-[var(--mag)]"}`}>
                      {on && <Check className="h-4 w-4 text-white" />}
                    </span>
                    <span>
                      <span className="block text-base font-semibold text-white">{l.name}</span>
                      <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">{l.cat}</span>
                    </span>
                  </span>
                  <span className={`font-mono-x text-lg font-bold ${on ? "text-[var(--pink-text)]" : "text-white"}`}>{money(l.price)}</span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
